import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const COLORS = {
  bg: '#0a0a0f', surface: '#12121a', border: '#1e1e2e',
  text: '#e8e8ed', textDim: '#8888a0', textMuted: '#55556a',
  logos: '#6366F1', gold: '#F59E0B', viability: '#10B981',
};
const fonts = { mono: "'JetBrains Mono', 'SF Mono', monospace", sans: "'Inter', sans-serif", display: "'Sora', sans-serif" };

const LESSON_ICONS = ['📻', '🌈', '🚦', '📡', '📊', '🎲', '📈'];

const LS_DAY_KEY = 'logos-lesson-day';
const LS_DATE_KEY = 'logos-lesson-last-date';

function getLessonState() {
  try {
    const day = parseInt(localStorage.getItem(LS_DAY_KEY) || '0');
    const lastDate = localStorage.getItem(LS_DATE_KEY) || '';
    return { day, lastDate };
  } catch { return { day: 0, lastDate: '' }; }
}

function advanceLesson() {
  try {
    const { day } = getLessonState();
    const next = day + 1;
    localStorage.setItem(LS_DAY_KEY, String(next));
    localStorage.setItem(LS_DATE_KEY, new Date().toISOString().slice(0, 10));
  } catch {}
}

export function shouldShowLesson() {
  const { day, lastDate } = getLessonState();
  if (day >= 7) return false;
  const today = new Date().toISOString().slice(0, 10);
  return lastDate !== today;
}

export function getCurrentLessonDay() {
  return getLessonState().day;
}

export default function MicroLesson({ onClose }) {
  const { t } = useTranslation();
  const lessonDay = getCurrentLessonDay();
  const [step, setStep] = useState(0); // 0 = lesson content, could extend

  if (lessonDay >= 7) { onClose(); return null; }

  const icon = LESSON_ICONS[lessonDay] || '✦';
  const title = t(`microLessons.${lessonDay}.title`, `Lesson ${lessonDay + 1}`);
  const body = t(`microLessons.${lessonDay}.body`, '');
  const highlight = t(`microLessons.${lessonDay}.highlight`, '');
  const dayLabel = t('microLessons.dayLabel', 'Day {{n}} of 7', { n: lessonDay + 1 });

  const handleComplete = () => {
    advanceLesson();
    onClose();
  };

  const handleSkip = () => {
    advanceLesson();
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9997,
      background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(10px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeInUp 0.3s ease',
    }}>
      <div style={{
        width: '90%', maxWidth: 440,
        background: `linear-gradient(145deg, ${COLORS.surface}, ${COLORS.bg})`,
        border: `1px solid ${COLORS.logos}25`, borderRadius: 18,
        padding: '24px 22px', position: 'relative',
        boxShadow: `0 0 80px ${COLORS.logos}12`,
      }}>
        {/* Progress dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 16 }}>
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} style={{
              width: i === lessonDay ? 18 : 8, height: 8, borderRadius: 4,
              background: i < lessonDay ? COLORS.viability : i === lessonDay ? COLORS.logos : COLORS.border,
              transition: 'all 0.3s',
            }} />
          ))}
        </div>

        {/* Day label */}
        <div style={{
          textAlign: 'center', fontSize: 9, fontFamily: fonts.mono, color: COLORS.logos,
          letterSpacing: 2, marginBottom: 8, textTransform: 'uppercase',
        }}>
          {dayLabel}
        </div>

        {/* Icon */}
        <div style={{ textAlign: 'center', fontSize: 48, marginBottom: 10 }}>
          {icon}
        </div>

        {/* Title */}
        <h3 style={{
          textAlign: 'center', fontSize: 18, fontWeight: 700, margin: '0 0 14px 0',
          fontFamily: fonts.display, color: COLORS.text,
        }}>
          {title}
        </h3>

        {/* Body */}
        <div style={{
          fontSize: 13, lineHeight: 1.7, color: COLORS.textDim,
          fontFamily: fonts.sans, whiteSpace: 'pre-line', marginBottom: 14,
        }}>
          {body}
        </div>

        {/* Highlight */}
        {highlight && (
          <div style={{
            padding: '12px 14px', background: `${COLORS.gold}10`,
            border: `1px solid ${COLORS.gold}25`, borderRadius: 10,
            marginBottom: 20,
          }}>
            <div style={{
              fontSize: 12, lineHeight: 1.55, color: COLORS.gold,
              fontFamily: fonts.sans, fontWeight: 500,
            }}>
              ✦ {highlight}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSkip} style={{
            flex: 1, padding: '11px 16px', borderRadius: 10, cursor: 'pointer',
            background: 'transparent', border: `1px solid ${COLORS.border}`,
            color: COLORS.textMuted, fontFamily: fonts.mono, fontSize: 11,
          }}>
            {t('microLessons.skip', 'Ver después')}
          </button>
          <button onClick={handleComplete} style={{
            flex: 2, padding: '11px 20px', borderRadius: 10, cursor: 'pointer',
            background: `linear-gradient(135deg, ${COLORS.logos}90, ${COLORS.logos})`,
            border: 'none', color: '#fff', fontFamily: fonts.mono, fontSize: 12,
            fontWeight: 600, letterSpacing: 1,
            boxShadow: `0 4px 20px ${COLORS.logos}35`,
          }}>
            {t('microLessons.understood', 'Entendido ✦')}
          </button>
        </div>
      </div>
    </div>
  );
}
